import { ReactElement, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { A_TO_Z_SEARCH_RESULT_CONTAINER_ID } from 'utils/constants/a-to-z';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fetchMenuData } from 'utils/generic/fetch-menu-data';
import { lazy } from 'utils/generic/lazy';
import { getSearchType } from 'utils/generic/searchType';
import { TAtoZMenuConfigs } from 'interfaces/generic';
import { DataPages } from './DataPages';
import { DefaultPages } from './DefaultPages';
import { AtoZSkeleton } from './Skeleton';
import { SearchElement } from 'newelements/SearchElement';
import { EAtoZEventBusActionTypes } from 'components/AtoZMenuContent/types';
import { LanguageSwitcherSkeleton } from 'components/Shared/LanguageSwitcher/Skeleton';
import { ChangeThemeSkeleton } from 'newcomponents/Shared/account/ChangeTheme/ChangeThemeSkeleton';
import { useEventbus } from 'newhooks/generic/useEventbus';
import { getMenuJsonData } from 'store/selectors/app-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const { LanguageSwitcher } = lazy(
  () =>
    import(
      /* webpackChunkName: "language-switcher" */ 'components/Shared/LanguageSwitcher'
    )
);

const { ChangeTheme } = lazy(
  () =>
    import(
      /* webpackChunkName: "change-theme-switcher" */ 'newcomponents/Shared/account/ChangeTheme'
    )
);

type TAtoZMenuContentProps = {
  setVisible: (arg: boolean) => void;
  setOpen: (arg: boolean) => void;
  configs: TAtoZMenuConfigs;
  open: boolean;
};

type TInfo = {
  add_minutes: string;
  current_game_state: string;
  current_game_time: string;
  field: number;
  pass_team: string;
  score1: string;
  score2: string;
  shirt1_color: string;
  shirt2_color: string;
  short1_color: string;
  short2_color: string;
  stoppage_firsthalf: string;
  stoppage_secondhalf: string;
};

type TGame = {
  id: number;
  info: TInfo;
  region_alias: string;
  start_ts: number;
  team1_name: string;
  team2_name: string;
  type: number;
};
type TCompetition = {
  id: number;
  name: string;
  game: Record<number, TGame>;
};
export type TSportsGame = {
  alias: 'Soccer';
  competition: Array<Record<number, TCompetition>>;
};

export const AtoZMenuContent = ({
  setOpen,
  setVisible,
  open,
  configs
}: TAtoZMenuContentProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const data = useSelector(getMenuJsonData);
  const [page, setPage] = useState(0);

  const [loading, setLoading] = useState(!data.length);

  useEffect(() => {
    if (!data.length) {
      fetchMenuData(setLoading);
    }
  }, []);

  useEventbus({
    [EAtoZEventBusActionTypes.CLOSE]: () => {
      setVisible?.(false);
    }
  });

  return (
    <div className="az-searchbar-container" ref={wrapperRef}>
      <div className="atozSearchWrapper">
        <SearchElement
          ignoreRouteProviders
          searchResultPortalId={A_TO_Z_SEARCH_RESULT_CONTAINER_ID}
          configs={{
            searchType: getSearchType(),
            racingSportsList: configs.aToZRacingSportsList
          }}
        />

        {!!configs?.showLangSwitcher && (
          <div className="atozSearchWrapper__langWrapper">
            <Suspense fallback={<LanguageSwitcherSkeleton />}>
              <LanguageSwitcher configs={configs} />
            </Suspense>
          </div>
        )}
        {SpringConfigs.THEME_SWITCHER && (
          <div className="atozSearchWrapper__changeTheme">
            <Suspense fallback={<ChangeThemeSkeleton />}>
              <ChangeTheme />
            </Suspense>
          </div>
        )}
      </div>

      <div id={A_TO_Z_SEARCH_RESULT_CONTAINER_ID} />

      {loading ? (
        <AtoZSkeleton
          excludeSpin
          sliderArrowsAToZ={configs.sliderArrowsAToZ}
          includeSearch={false}
        />
      ) : data.length ? (
        <DataPages
          page={page}
          open={open}
          setPage={setPage}
          data={data}
          setVisible={setVisible}
          setOpen={setOpen}
          showActivePage={configs?.selectCurrentPage}
          configs={configs}
        />
      ) : (
        <DefaultPages
          page={page}
          setPage={setPage}
          setVisible={setVisible}
          setOpen={setOpen}
          configs={configs}
        />
      )}
    </div>
  );
};
